import { fetchEventSource } from "@microsoft/fetch-event-source";

import { AskQuestionRequest, ChatMessage, ChatMetadata, MaiteChatMessage } from "../types";
import { Components, Paths } from "./schema/openapi";
import { useApiClient } from "./useApiClient";

// Re-export generated API types for convenience
export type { Components, Paths };

// Type aliases from generated schema
export type ApiChatMessage = Components.Schemas.QueryResponseV1;
export type ApiChatMetadata = Components.Schemas.MetadataResponse;
export type ApiAskQuestionRequest = Components.Schemas.AskQuestionRequest;
export type ApiAskQuestionResponse = Components.Schemas.QueryResponseV1;
export type ApiStreamQuestionResponse = Components.Schemas.StreamQuestionResponseDto;
export type ApiStreamQuestionRequest = Components.Schemas.StreamQuestionRequestDto;
export type ApiStructuredContent = Components.Schemas.StructuredContentDto;

// Content block type aliases
export type ApiTextBlock = Components.Schemas.TextBlockDto;
export type ApiHeadingBlock = Components.Schemas.HeadingBlockDto;
export type ApiListBlock = Components.Schemas.ListBlockDto;
export type ApiMetricBlock = Components.Schemas.MetricBlockDto;
export type ApiInsightBlock = Components.Schemas.InsightBlockDto;
export type ApiRecommendationBlock = Components.Schemas.RecommendationBlockDto;
export type ApiCalloutBlock = Components.Schemas.CalloutBlockDto;
export type ApiDividerBlock = Components.Schemas.DividerBlockDto;
export type ApiChartBlock = Components.Schemas.ChartBlockDto;
export type ApiTableBlock = Components.Schemas.TableBlockDto;

// API functions using the generated OpenAPI client
export const chatApi = {
  askQuestion: (client: ReturnType<typeof useApiClient>, data: AskQuestionRequest) => {
    const apiRequest: ApiAskQuestionRequest = {
      question: data.question,
      chat_id: data.chatId,
      documentIds: data.documentIds,
      model: data.model as "CLAUDE" | "GEMINI",
      warcEnabled: data.warcEnabled,
    };
    return client.api.askQuestion(null, apiRequest);
  },

  getChatMessages: (client: ReturnType<typeof useApiClient>, chatId: string) =>
    client.api.getChatMessagesV1({ chatId }),

  submitFeedback: (
    client: ReturnType<typeof useApiClient>,
    chatId: string,
    messageId: string,
    data: { score: 1 | -1; modelUsed: "CLAUDE" | "GEMINI" }
  ) =>
    client.api.submitFeedback(
      { chatId, messageId },
      {
        score: data.score,
        modelUsed: data.modelUsed as "CLAUDE" | "GEMINI",
      }
    ),

  getChatMetadata: (client: ReturnType<typeof useApiClient>) => client.api.getMetadata(),

  getChats: (client: ReturnType<typeof useApiClient>) => client.api.getChatsV1(),

  // Stream question using fetchEventSource
  streamQuestion: async (
    client: ReturnType<typeof useApiClient>,
    data: ApiStreamQuestionRequest,
    accessToken: string,
    callbacks: {
      onAgentUpdate: (message: { chatId: string; preSetConstraints?: any }) => void;
      onAgentStep: (message: { message: string; agentName: string }) => void;
      onFinalResult: (result: {
        chatId: string;
        messageId: string;
        preSetConstraints?: any;
        finalMessage: {
          id: string;
          chatId: string;
          user: "maite";
          isError: false;
          isScenariosQuery: boolean;
          structuredContent?: ApiStructuredContent;
        };
      }) => void;
      onError: (result: {
        chatId: string;
        messageId: string;
        finalMessage: {
          id: string;
          chatId: string;
          user: "maite";
          isError: true;
          isScenariosQuery: boolean;
          structuredContent?: ApiStructuredContent;
          error: string;
          message: string;
        };
      }) => void;
    },
    signal?: AbortSignal
  ): Promise<void> => {
    const baseURL = client.api.defaults?.baseURL || "";

    // Build query parameters for GET request
    const queryParams = new URLSearchParams({
      question: data.question,
    });

    if (data.chatId) {
      queryParams.append("chatId", data.chatId);
    }

    if (data.documentIds && data.documentIds.length > 0) {
      queryParams.append("documentIds", data.documentIds.join(","));
    }

    if (data.model) {
      queryParams.append("model", data.model);
    }

    if (typeof data.warcEnabled === "boolean") {
      queryParams.append("warcEnabled", data.warcEnabled.toString());
    }

    if (data.brand) {
      queryParams.append("brand", data.brand);
    }

    if (data.category) {
      queryParams.append("category", data.category);
    }

    if (data.product) {
      queryParams.append("product", data.product);
    }

    if (data.startDate) {
      queryParams.append("startDate", data.startDate);
    }

    if (data.endDate) {
      queryParams.append("endDate", data.endDate);
    }

    const url = `${baseURL}/api/v2/question/stream?${queryParams.toString()}`;

    const headers: Record<string, string> = {
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${accessToken}`,
    };

    // Utility to parse SSE event data
    const parseSSEEvent = (eventData: string): ApiStreamQuestionResponse | null => {
      if (!eventData.trim()) return null;

      // Validate that we have what looks like complete JSON
      if (!eventData.startsWith("{") || !eventData.endsWith("}")) {
        return null; // Incomplete JSON, skip silently
      }

      try {
        return JSON.parse(eventData) as ApiStreamQuestionResponse;
      } catch (error) {
        console.warn("Failed to parse SSE JSON:", error);
        return null;
      }
    };

    await fetchEventSource(url, {
      method: "GET",
      headers,
      signal,
      openWhenHidden: true,
      onopen: async (response) => {
        if (response.ok) {
          return;
        } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      },
      onmessage: (event) => {
        const response = parseSSEEvent(event.data);
        if (!response) return;

        // Handle only the 3 events we care about
        switch (response.type) {
          case "AgentUpdate":
            callbacks.onAgentUpdate({
              chatId: response.chatId,
              preSetConstraints: response.preSetConstraints,
            });
            break;

          case "AgentStepEvent":
            callbacks.onAgentStep({ message: response.message, agentName: response.agentName });

            break;

          case "FinalResult": {
            // Defensive parsing - handle agent nesting mistakes and edge cases
            const parsedStructuredContent = response.structuredContent
              ? parseStructuredContent(response.structuredContent)
              : undefined;

            callbacks.onFinalResult({
              chatId: response.chatId,
              messageId: response.queryResponseId,
              preSetConstraints: response.preSetConstraints,
              finalMessage: {
                id: response.queryResponseId,
                chatId: response.chatId,
                user: "maite",
                isError: false,
                isScenariosQuery: response.isScenariosQuery ?? false,
                ...(parsedStructuredContent && {
                  structuredContent: parsedStructuredContent,
                }),
              },
            });
            break;
          }

          case "WorkflowError": {
            // Defensive parsing - handle agent nesting mistakes and edge cases
            const parsedStructuredContent = response.structuredContent
              ? parseStructuredContent(response.structuredContent)
              : undefined;

            callbacks.onError({
              chatId: response.chatId,
              messageId: response.queryResponseId,
              finalMessage: {
                id: response.queryResponseId,
                chatId: response.chatId,
                user: "maite",
                isError: true,
                isScenariosQuery: response.isScenariosQuery ?? false,
                message: response.message,
                error: response.errorMessage,
                ...(parsedStructuredContent && {
                  structuredContent: parsedStructuredContent,
                }),
              },
            });
            break;
          }

          // Ignore all other events
          default:
            break;
        }
      },
      onclose: () => {},
      onerror: (error) => {
        console.error("Stream error:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        callbacks.onError({
          chatId: data.chatId || "",
          messageId: "",
          finalMessage: {
            id: "",
            chatId: data.chatId || "",
            user: "maite",
            isError: true,
            isScenariosQuery: false, // Network errors don't have scenario context
            error: errorMessage,
            message: `Stream connection error: ${errorMessage}`,
          },
        });
        throw error;
      },
    });
  },

  createChat: (client: ReturnType<typeof useApiClient>) => client.api.createChat(),
};

/**
 * Parses and validates structured content, handling edge cases:
 * 1. Already-parsed object (validate and return)
 * 2. Stringified JSON (single or double stringification)
 * 3. Nested JSON inside a text block's content field (agent mistake)
 * 4. Invalid/malformed JSON
 */
const parseStructuredContent = (
  content: string | ApiStructuredContent
): ApiStructuredContent | undefined => {
  // Case 1: Already an object - validate structure and return
  if (typeof content === "object" && content !== null) {
    const hasValidStructure =
      ("response" in content && Array.isArray(content.response)) ||
      ("detailed" in content && Array.isArray(content.detailed)) ||
      ("graphs" in content && Array.isArray(content.graphs)) ||
      "conversation" in content ||
      "analysis" in content;

    if (hasValidStructure) {
      return content as ApiStructuredContent;
    }
    return undefined;
  }

  // Case 2+: String parsing
  if (typeof content !== "string" || !content?.trim().startsWith("{")) {
    return undefined;
  }

  try {
    let parsed: unknown = JSON.parse(content);

    // Handle double stringification - parse again if still a string
    if (typeof parsed === "string" && parsed.trim().startsWith("{")) {
      parsed = JSON.parse(parsed);
    }

    if (!parsed || typeof parsed !== "object") {
      return undefined;
    }

    const parsedObj = parsed as Record<string, unknown>;

    // Case 3: Detect agent mistake - nested structured content inside a text block
    // Pattern: {"response": [{"type": "text", "content": "{\"response\":[...]}"}]}
    if (
      parsedObj.response &&
      Array.isArray(parsedObj.response) &&
      parsedObj.response.length > 0 &&
      typeof parsedObj.response[0] === "object" &&
      parsedObj.response[0] !== null
    ) {
      const firstBlock = parsedObj.response[0] as Record<string, unknown>;

      // Check if this is a text block containing stringified structured content
      if (
        firstBlock.type === "text" &&
        typeof firstBlock.content === "string" &&
        firstBlock.content.trim().startsWith("{") &&
        (firstBlock.content.includes('"response"') ||
          firstBlock.content.includes('\\"response\\"') ||
          firstBlock.content.includes('"detailed"') ||
          firstBlock.content.includes('\\"detailed\\"'))
      ) {
        try {
          // Parse the nested content directly
          const nestedContent = firstBlock.content;
          const extractedContent = JSON.parse(nestedContent);

          if (typeof extractedContent === "object" && extractedContent !== null) {
            console.log("[parseStructuredContent] ✅ Successfully extracted nested content");
            // Recursively validate the extracted content
            return parseStructuredContent(extractedContent as ApiStructuredContent);
          }
        } catch (extractError) {
          console.error(
            "[parseStructuredContent] ❌ Failed to extract nested content:",
            extractError
          );
          // Return undefined - don't use the malformed wrapper
          return undefined;
        }
      }
    }

    // Validate structure - support both old and new formats
    const hasOldConversation =
      parsedObj.conversation &&
      typeof parsedObj.conversation === "object" &&
      "text" in parsedObj.conversation &&
      Array.isArray((parsedObj.conversation as Record<string, unknown>).text);

    const hasOldAnalysis =
      parsedObj.analysis &&
      typeof parsedObj.analysis === "object" &&
      "text" in parsedObj.analysis &&
      Array.isArray((parsedObj.analysis as Record<string, unknown>).text);

    const hasAnalysis =
      (parsedObj.detailed && Array.isArray(parsedObj.detailed)) ||
      (parsedObj.graphs && Array.isArray(parsedObj.graphs));

    const hasConversation = parsedObj.response && Array.isArray(parsedObj.response);

    console.log(hasAnalysis, hasConversation, hasOldConversation, hasOldAnalysis);

    if (hasAnalysis || hasConversation || hasOldConversation || hasOldAnalysis) {
      return parsed as ApiStructuredContent;
    }
    return undefined;
  } catch (error) {
    console.error("Error parsing structured content:", error);
    return undefined;
  }
};

const createUserMessage = (query: string, preSetConstraints?: any): ChatMessage => ({
  user: "user",
  content: query,
  preSetConstraints,
});

const createMaiteMessage = (
  apiMessage: ApiChatMessage,
  structuredContent?: ApiStructuredContent
): MaiteChatMessage => ({
  id: apiMessage.id,
  chatId: apiMessage.chatId,
  user: "maite",
  isError: apiMessage.isError ?? false,
  error: apiMessage.error,
  content: apiMessage.content,
  structuredContent,
  feedbackScore: apiMessage.score ? (apiMessage.score > 0 ? "positive" : "negative") : undefined,
  debug: apiMessage.debug,
  isScenariosQuery: apiMessage.detail?.isScenariosQuery ?? false,
  preSetConstraints: apiMessage.detail?.preSetConstraints,
});

export const transformApiChatMessage = (apiMessage: ApiChatMessage): ChatMessage[] => {
  const structuredContent = parseStructuredContent(apiMessage.content);
  const preSetConstraints = apiMessage.detail?.preSetConstraints;
  return [
    createUserMessage(apiMessage.query, preSetConstraints),
    createMaiteMessage(apiMessage, structuredContent),
  ];
};

export const transformApiChatMetadata = (apiMetadata: ApiChatMetadata): ChatMetadata => {
  const isPremium = apiMetadata.hendrenTier === "PREMIUM";
  return {
    hendrenTier: apiMetadata.hendrenTier as "FREE" | "PREMIUM" | "OFF",
    monthlyQueryLimit: apiMetadata.rateLimit.monthlyQueryLimit,
    remainingQueries:
      apiMetadata.rateLimit.monthlyQueryLimit - apiMetadata.rateLimit.monthlyQueryCount,
    queryLimitReached:
      !isPremium &&
      apiMetadata.rateLimit.monthlyQueryCount >= apiMetadata.rateLimit.monthlyQueryLimit,
    availableModels: apiMetadata.availableModels,
  };
};
